import { AsyncPipe } from '@angular/common';
import { ChangeDetectionStrategy, Component, OnInit } from '@angular/core';
import { FormsModule, ReactiveFormsModule, UntypedFormBuilder, UntypedFormGroup } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { TranslocoModule } from '@jsverse/transloco';
import { PaymentMethodService } from '@rma/dashboard/billing/public/api-payment-methods';
import { StandardErrorMessage } from '@rma/dashboard/generic/domain/to-be-sorted';
import { StripeModule, StripeService } from '@rma/dashboard/generic/orc/stripe';
import { MatErrorOverrideComponent } from '@rma/dashboard/generic/ui/form-error';
import { LoadingIndicatorDirective } from '@rma/dashboard/generic/ui/loading-indicator';
import { RmaIconComponent } from '@rma/generic/ui/icon';
import { SetupIntentResult, StripeElements, StripeError, StripePaymentElementChangeEvent } from '@stripe/stripe-js';
import { catchError, map, Observable, of, Subject, switchMap } from 'rxjs';

@Component({
  templateUrl: './add-payment-method-dialog.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    AsyncPipe,
    FormsModule,
    LoadingIndicatorDirective,
    MatButtonModule,
    MatCheckboxModule,
    MatDialogModule,
    MatErrorOverrideComponent,
    RmaIconComponent,
    MatProgressSpinnerModule,
    ReactiveFormsModule,
    StripeModule,
    TranslocoModule,
  ],
})
export class AddPaymentMethodDialogComponent implements OnInit {
  public isComplete: boolean;
  public form: UntypedFormGroup;
  public clientSecret$?: Observable<string | null>;

  public isSubmitting$ = new Subject<boolean>();
  public error$? = new Subject<string>();

  private stripeElements: StripeElements;

  public constructor(
    private readonly formBuilder: UntypedFormBuilder,
    private readonly dialogRef: MatDialogRef<AddPaymentMethodDialogComponent>,
    private readonly stripeService: StripeService,
    private readonly paymentMethodService: PaymentMethodService,
  ) {}

  public ngOnInit(): void {
    this.form = this.formBuilder.group({
      isDefault: true,
    });
    this.clientSecret$ = this.paymentMethodService.setupIntent().pipe(
      catchError((x) => {
        this.error$?.next(x.message ?? StandardErrorMessage);
        return of(null);
      }),
    );
  }

  public onStripeElementLoaded(stripeElements: StripeElements) {
    this.stripeElements = stripeElements;
  }

  public onStripeElementChanged($event: StripePaymentElementChangeEvent): void {
    this.isComplete = $event.complete;
  }

  public onSubmit() {
    this.isSubmitting$.next(true);

    const setAsDefault: boolean = this.form.value.isDefault;

    this.stripeService
      .confirmSetup(this.stripeElements, `${location.origin}${location.pathname}?is_default=${setAsDefault}`)
      .pipe(
        map((stripeResponse: SetupIntentResult) => {
          if (stripeResponse.error) {
            throw stripeResponse.error;
          }
          return stripeResponse.setupIntent;
        }),
        switchMap((x) =>
          setAsDefault && x.status === 'succeeded' && x.payment_method
            ? // SAFETY(legacy): unreviewed, pre-dates this rule.
              this.paymentMethodService.setDefaultPaymentMethod(x.payment_method as string).pipe(catchError(() => of(null)))
            : of(null),
        ),
      )
      .subscribe(
        (_) => {
          this.dialogRef.close(true);
        },
        (err: StripeError) => {
          this.isSubmitting$.next(false);
          this.error$?.next(err.message ?? StandardErrorMessage);
        },
      );
  }
}
